<template>
    <div
        class="d-flex pa-0 ma-0 fill-height align-self-end ga-0"
    >
        <v-btn
            color="accent-darken-1"
            to="/cart"
            rounded="0"
            variant="elevated"
            size="x-large"
            width="35%"
            flat
        >
            Cart
        </v-btn>
        <!--
            When the cart holds unpurchasable lines the button stays enabled but
            drops its `to` — it opens the acknowledgement modal instead of
            navigating to a checkout that cannot be paid for.
        -->
        <v-btn
            color="primary"
            :to="hasCheckoutBlockingLines ? undefined : '/checkout'"
            :disabled="!canCheckout"
            rounded="0"
            variant="elevated"
            size="x-large"
            width="65%"
            flat
            @click="onCheckoutClick"
        >
            Checkout
        </v-btn>

        <!--
            Only in the tree when there is something to acknowledge. Rendering
            on the flag rather than on `dialogBlockedItems` pulls the async
            chunk in as soon as the cart is known to be blocked, so the click
            opens instantly.
        -->
        <CartBlockedItemsModal
            v-if="hasCheckoutBlockingLines || dialogBlockedItems"
            v-model="dialogBlockedItems"
            confirm-text="Remove and Continue"
            @on-acknowledged="onBlockedItemsAcknowledged"
            @on-cancel="onBlockedItemsCancel"
        ></CartBlockedItemsModal>
    </div>
</template>

<script lang="ts" setup>
    import { useCarts } from '~/composables';
    import type { Cart } from '~/types';

    const CartBlockedItemsModal = defineAsyncComponent(() => import('~/components/cart/CartBlockedItemsModal/CartBlockedItemsModal.vue'));

    const {
        canCheckout,
        hasCheckoutBlockingLines
    } = useCarts();

    const dialogBlockedItems = ref<boolean>(false);

    function onCheckoutClick(): void {
        if (hasCheckoutBlockingLines.value) {
            dialogBlockedItems.value = true;
        }
    }

    // Removing every blocking line can empty the cart — only continue to
    // checkout when something is left to pay for.
    async function onBlockedItemsAcknowledged(cartObj: Cart): Promise<void> {
        if (cartObj.lineCount > 0) {
            await navigateTo('/checkout');
        } else {
            await navigateTo('/cart');
        }
    }

    // Declining from the drawer would otherwise just close the modal and leave
    // the customer where they started — send them to the cart, where the
    // blocked lines are badged inline.
    async function onBlockedItemsCancel(): Promise<void> {
        await navigateTo('/cart');
    }
</script>

<style lang="scss" scoped></style>
